import { Check } from 'lucide-react';

import { IconBadgeVerified, IconBadgeValidated, IconBadgeCertified } from 'src/components/Svg/Images';
import { cn } from '@/lib/utils';

const certificationItemsMap = {
  validated: {
    id: 'validated',
    label: 'Validated',
    icon: IconBadgeValidated,
  },
  verified: {
    id: 'verified',
    label: 'Verified',
    icon: IconBadgeVerified,
  },
  certified: {
    id: 'certified',
    label: 'Certified',
    icon: IconBadgeCertified,
  },
} satisfies Record<string, { id: string; label: string; icon: React.ElementType }>;

type CertificationKey = keyof typeof certificationItemsMap;

type Props<
  TStep extends {
    value: string;
    label: string;
  },
> = {
  steps: TStep[];
  activeStep: TStep['value'];
  certificationKeys?: readonly CertificationKey[];
  certificationLabel?: string;
};

const defaultCertificationKeys: readonly CertificationKey[] = ['validated', 'verified', 'certified'];

const ActivateProgressSteps = <
  TStep extends {
    value: string;
    label: string;
  },
>({
  steps,
  activeStep,
  certificationKeys = defaultCertificationKeys,
  certificationLabel,
}: Props<TStep>) => {
  const activeStepIndex = steps.findIndex((step) => step.value === activeStep);

  return (
    <div className="flex relative items-center z-1">
      <div className="flex grow">
        {steps.map(({ label, value }, index) => {
          const isActive = index === activeStepIndex;
          const isCompleted = index < activeStepIndex;

          return (
            <div key={value} className="group flex flex-col gap-2 flex-1 items-start py-0.5">
              <div
                className={cn(
                  'relative w-full after:absolute after:bg-slate-300 group-first:after:left-3 after:left-0 after:right-0 after:h-1 after:top-1/2 after:-translate-y-1/2 after:z-0',
                  isCompleted && 'after:bg-secondary-foreground',
                )}
              >
                <div
                  className={cn(
                    'relative w-3.5 h-6 flex items-center justify-center before:size-full before:border-2 before:border-secondary-foreground before:rounded-full before:z-1 before:absolute before:left-0 before:top-0 before:scale-0 before:transition-transform before:duration-150 before:ease-in-out',
                    isActive && 'w-6 before:scale-100',
                  )}
                >
                  <span
                    className={cn(
                      'relative size-3.5 bg-slate-300 rounded-full flex items-center justify-center z-1',
                      (isCompleted || isActive) && 'bg-secondary-foreground',
                    )}
                  >
                    {isCompleted && <Check className="size-[10px] text-white" />}
                  </span>
                </div>
              </div>
              <span
                className={cn(
                  'text-[10px]',
                  isCompleted || isActive ? 'text-foreground font-semibold' : 'text-gray-500',
                )}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      <CertificationStep certificationKeys={certificationKeys} certificationLabel={certificationLabel} />
    </div>
  );
};

const CertificationStep = ({
  certificationKeys,
  certificationLabel = 'Verifications',
}: {
  certificationKeys: readonly CertificationKey[];
  certificationLabel?: string;
}) => {
  return (
    <div className={cn('shrink-0 flex flex-col items-center gap-1', certificationKeys.length === 1 && 'items-end')}>
      <div
        className={cn(
          'relative flex items-center gap-2 before:absolute before:bg-slate-300 before:w-full before:h-1 before:left-0 before:top-1/2 before:-translate-y-1/2 before:z-0 after:absolute after:bg-secondary-foreground after:h-0.5 after:left-0 after:top-1/2 after:-translate-y-1/2 after:z-0',
          certificationKeys.length === 1 && 'before:-left-full before:top-3.75',
        )}
      >
        {certificationKeys.map((key) => {
          const { icon: Icon } = certificationItemsMap[key];

          return (
            <div
              key={key}
              data-testid={`certification-badge-${key}`}
              className="size-8 bg-white rounded-full relative z-1"
            >
              <Icon className="size-full opacity-30" />
            </div>
          );
        })}
      </div>
      <div className="text-xs text-center text-gray-500">{certificationLabel}</div>
    </div>
  );
};

export default ActivateProgressSteps;
